/* OrganisationPortal.tsx - restore centered, separated "Complete Form" button */

import React, { useEffect, useState, useMemo } from "react";
import { useParams, useLocation, Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Navbar from "../components/Navbar";
import MessageList, { Message } from "../components/MessageList";
import FormModal from "../components/FormModal";
import ReplyModal from "../components/ReplyModal";
import ConfirmationModal from "../components/ConfirmationModal";
import "../styles/OrganisationPortal.css";
import Form from "@rjsf/core";
import validator from "@rjsf/validator-ajv8";
import Ajv from "ajv";
import addFormats from "ajv-formats";

const ajv = new Ajv({ strict: false, allErrors: true });
addFormats(ajv);
(validator as any).ajv = ajv;
const customValidator = validator;

interface Organisation {
  pncID: string;
  organisationLabel: string;
  organisationSORPersonID?: string;
}

interface Report {
  recordType: string;
  recordType_cy?: string;
  description: string;
  description_cy?: string;
  officerDetails: string;
  dateCreated: string;
  dateCreated_cy?: string;
}

interface ApiResponse {
  status: string;
  data: {
    organisation: Organisation;
    report: Report;
    messages: Message[];
  };
}

const OrganisationPortal: React.FC = () => {
  const { organisationID, NCJPRecordID } = useParams<{ organisationID: string; NCJPRecordID: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const oneLoginSub = localStorage.getItem("oneLoginSub") || "";

  const { t, i18n } = useTranslation("translation");
  const translate = t as unknown as (key: string, options?: any) => string;
  const isWelsh = i18n.language === "cy";

  const [organisation, setOrganisation] = useState<Organisation | null>(null);
  const [report, setReport] = useState<Report | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [currentMessageIndex, setCurrentMessageIndex] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const [showModal, setShowModal] = useState<boolean>(false);
  const [formSchema, setFormSchema] = useState<any>(null);
  const [formUiSchema, setFormUiSchema] = useState<any>({});
  const [formData, setFormData] = useState<any>({});

  const [showReplyModal, setShowReplyModal] = useState<boolean>(false);
  const [replyText, setReplyText] = useState<string>("");
  const [replyMessage, setReplyMessage] = useState<Message | null>(null);

  const [showSaveConfirmation, setShowSaveConfirmation] = useState<boolean>(false);

  const effectiveSchema = useMemo(() => {
    if (formSchema) {
      let newSchema: any;
      if (typeof formSchema === "string") {
        try {
          newSchema = JSON.parse(formSchema);
        } catch (err) {
          console.error("Error parsing formSchema:", err);
          newSchema = {};
        }
      } else {
        newSchema = { ...formSchema };
      }
      if (newSchema.$schema?.startsWith("http://")) {
        newSchema.$schema = newSchema.$schema.replace("http://", "https://");
      }
      return newSchema;
    }
    return {};
  }, [formSchema]);

  const effectiveUiSchema = useMemo(() => {
    let parsedUiSchema: any = {};
    if (formUiSchema) {
      try {
        parsedUiSchema = typeof formUiSchema === "string" ? JSON.parse(formUiSchema) : { ...formUiSchema };
        if (parsedUiSchema.uiSchema) parsedUiSchema = parsedUiSchema.uiSchema;
      } catch (err) {
        console.error("Error parsing formUiSchema:", err);
      }
    }
    parsedUiSchema["ui:submitButtonOptions"] = { norender: true };
    return parsedUiSchema;
  }, [formUiSchema]);

  useEffect(() => {
    fetch(
      `https://api.psanalytics.co.uk/ncjp/getOrganisationPortalDetails?organisationID=${encodeURIComponent(organisationID || "")}&NCJPRecordID=${encodeURIComponent(NCJPRecordID || "")}&oneLoginSub=${encodeURIComponent(oneLoginSub)}`
    )
      .then(res => res.json())
      .then((data: ApiResponse) => {
        if (data.status === "success") {
          setOrganisation(data.data.organisation);
          setReport(data.data.report);
          setMessages(data.data.messages || []);
        }
      })
      .catch(console.error)
      .finally(() => setIsLoading(false));
  }, [organisationID, NCJPRecordID, oneLoginSub]);

  const handlePrevMessage = () => currentMessageIndex > 0 && setCurrentMessageIndex(i => i - 1);
  const handleNextMessage = () => currentMessageIndex < messages.length - 1 && setCurrentMessageIndex(i => i + 1);
  const handleReply = (msg: Message) => (setReplyMessage(msg), setShowReplyModal(true));

  const handleToggleRead = (ncjpMessageID: string, currentState: boolean) => {
    fetch(`https://api.psanalytics.co.uk/ncjp/toggleMessageRead.php?ncjpMessageID=${encodeURIComponent(ncjpMessageID)}`, {
      method: "POST",
      credentials: "include",
    })
      .then(res => res.json())
      .then(data => {
        if (data.status === "success") {
          setMessages(prev =>
            prev.map(msg =>
              msg.ncjpMessageID === ncjpMessageID ? { ...msg, messageRead: data.newState === 1 } : msg
            )
          );
        }
      })
      .catch(console.error);
  };

  const handleCompleteForm = (message: Message) => {
    if (message.formSchema) {
      setFormSchema(message.formSchema);
      setFormUiSchema(message.formUiSchema || {});
      setShowModal(true);
    }
  };

  return (
    <div>
      <Navbar />
      <main className="org-portal-container">
        {isLoading ? (
          <div className="loading-overlay">
            <img src="/assets/images/spinner.svg" alt="Loading..." className="spinner-img" />
            <p>{translate("loadingRecord")}</p>
          </div>
        ) : (
          <>
            <nav className="govuk-breadcrumbs" aria-label="Breadcrumb">
              <ol className="govuk-breadcrumbs__list">
                <li className="govuk-breadcrumbs__list-item">
                  <Link className="govuk-breadcrumbs__link" to={`/?oneLoginSub=${encodeURIComponent(oneLoginSub)}`}>
                    {translate("dashboard")}
                  </Link>
                </li>
                <li className="govuk-breadcrumbs__list-item">
                  <span className="govuk-breadcrumbs__link govuk-breadcrumbs__link--current">
                    {translate("recordDetails")}
                  </span>
                </li>
              </ol>
            </nav>

            {organisation && (
              <div className="org-header-center">
                <img src={`/assets/crests/${organisation.pncID}.jpg`} alt="Organisation Crest" className="org-logo" />
                <h2 className="govuk-heading-l">{organisation.organisationLabel} {translate("publicPortal")}</h2>
              </div>
            )}

            {report && (
              <div className="org-report-details">
                <p className="govuk-body">
                  <strong>{translate("caseManager")}</strong> {report.officerDetails}
                </p>
                <p className="govuk-body">
                  <strong>{translate("reportType")}</strong> {isWelsh && report.recordType_cy ? report.recordType_cy : report.recordType}
                </p>
                <p className="govuk-body">
                  <strong>{translate("description")}</strong> {isWelsh && report.description_cy ? report.description_cy : report.description}
                </p>
              </div>
            )}

            {messages.length > 0 && (
              <div className="org-message-section">
                <div className="org-message-nav">
                  <button className="govuk-button org-prev-btn" onClick={handlePrevMessage} disabled={currentMessageIndex === 0}>
                    &lt; {translate("previous")}
                  </button>
                  <span className="org-message-counter">
                    {translate("message")} {currentMessageIndex + 1} {translate("of")} {messages.length}
                  </span>
                  <button className="govuk-button org-next-btn" onClick={handleNextMessage} disabled={currentMessageIndex === messages.length - 1}>
                    {translate("next")} &gt;
                  </button>
                </div>

                <div className="govuk-panel govuk-panel--grey org-message-panel">
                  <div className="govuk-panel__body">
                    <p className={`govuk-body ${messages[currentMessageIndex].messageRead ? "read" : "unread"}`} style={{ textAlign: "justify" }}>
                      <strong>{translate("message")} {currentMessageIndex + 1}:</strong>{" "}
                      {messages[currentMessageIndex].messageText}
                    </p>
                    <p className="govuk-body-s">
                      <strong>{translate("sent")}:</strong> {messages[currentMessageIndex].dateCreated}
                    </p>
                    <div className="org-message-btn-group">
                      <button className="govuk-button org-mark-btn" onClick={() => handleToggleRead(messages[currentMessageIndex].ncjpMessageID, messages[currentMessageIndex].messageRead || false)}>
                        {messages[currentMessageIndex].messageRead ? translate("markAsUnread") : translate("markAsRead")}
                      </button>
                      <button className="govuk-button org-reply-btn" onClick={() => handleReply(messages[currentMessageIndex])}>
                        {translate("reply")}
                      </button>
                    </div>
                    {messages[currentMessageIndex].formSchema && (
                      <div className="org-complete-form-container">
                        <button className="govuk-button org-complete-btn" onClick={() => handleCompleteForm(messages[currentMessageIndex])}>
                          {translate("completeForm")}
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            <div className="org-back-spacing"></div>
            <div className="org-back-btn">
              <Link to={`/?oneLoginSub=${encodeURIComponent(oneLoginSub)}`} className="govuk-button">
                &larr; {translate("backToDashboard")}
              </Link>
            </div>
          </>
        )}
      </main>

      <FormModal
        show={showModal}
        schema={effectiveSchema}
        uiSchema={effectiveUiSchema}
        formData={formData}
        onChange={(e: any) => setFormData(e.formData)}
        onSubmit={() => setShowModal(false)}
        onSaveAndResume={() => {}}
        onCancel={() => setShowModal(false)}
      />

      <ConfirmationModal show={showSaveConfirmation} onDismiss={() => setShowSaveConfirmation(false)} />

      {showReplyModal && replyMessage && (
        <ReplyModal
          show={showReplyModal}
          replyMessage={replyMessage}
          replyText={replyText}
          onChange={(e) => setReplyText(e.target.value)}
          onCancel={() => setShowReplyModal(false)}
          onSaveReply={() => {}}
          onSubmit={() => {}}
        />
      )}
    </div>
  );
};

export default OrganisationPortal;